import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface CadastralParcel {
  id: string;
  refCatastral: string;
  geometry: any;
  properties: {
    provincia: string;
    municipio: string;
    poligono: string;
    parcela: string;
    superficie: number;
    cultivo: string;
  };
}

export interface CadastralState {
  // Estado del servicio
  isInitialized: boolean;
  isReady: boolean;
  
  // Parcelas catastrales
  parcels: CadastralParcel[];
  currentParcel: CadastralParcel | null;
  selectedParcelId: string | null;
  
  // Referencias catastrales
  refCatastral: string;
  searchHistory: string[];
  
  // Datos de fincas
  fincas: any[];
  currentFinca: any | null;
  
  // Configuración
  showParcels: boolean;
  showFincas: boolean;
  
  // Operaciones
  isLoading: boolean;
  isSearching: boolean;
  error: string | null;
  
  // Acciones
  setInitialized: (initialized: boolean) => void;
  setReady: (ready: boolean) => void;
  addParcel: (parcel: CadastralParcel) => void;
  removeParcel: (parcelId: string) => void;
  setCurrentParcel: (parcel: CadastralParcel | null) => void;
  setSelectedParcelId: (parcelId: string | null) => void;
  updateParcel: (parcelId: string, updates: Partial<CadastralParcel>) => void;
  setRefCatastral: (refCatastral: string) => void;
  addToSearchHistory: (refCatastral: string) => void;
  clearSearchHistory: () => void;
  addFinca: (finca: any) => void;
  removeFinca: (fincaId: string) => void;
  setCurrentFinca: (finca: any | null) => void;
  updateFinca: (fincaId: string, updates: any) => void;
  toggleShowParcels: () => void;
  toggleShowFincas: () => void;
  setLoading: (loading: boolean) => void;
  setSearching: (searching: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  isInitialized: false,
  isReady: false,
  parcels: [],
  currentParcel: null,
  selectedParcelId: null,
  refCatastral: '',
  searchHistory: [],
  fincas: [],
  currentFinca: null,
  showParcels: true,
  showFincas: false,
  isLoading: false,
  isSearching: false,
  error: null,
};

export const useCadastralStore = create<CadastralState>()(
  devtools(
    (set, get) => ({
      ...initialState,
      
      setInitialized: (initialized) => set({ isInitialized: initialized }),
      setReady: (ready) => set({ isReady: ready }),
      
      addParcel: (parcel) => set((state) => ({
        parcels: [...state.parcels, parcel],
        currentParcel: parcel,
        selectedParcelId: parcel.id,
      })),
      
      removeParcel: (parcelId) => set((state) => ({
        parcels: state.parcels.filter(p => p.id !== parcelId),
        currentParcel: state.currentParcel?.id === parcelId ? null : state.currentParcel,
        selectedParcelId: state.selectedParcelId === parcelId ? null : state.selectedParcelId,
      })),
      
      setCurrentParcel: (parcel) => set({ currentParcel: parcel }),
      setSelectedParcelId: (parcelId) => set({ selectedParcelId: parcelId }),
      
      updateParcel: (parcelId, updates) => set((state) => ({
        parcels: state.parcels.map(p => p.id === parcelId ? { ...p, ...updates } : p),
        currentParcel: state.currentParcel?.id === parcelId ? { ...state.currentParcel, ...updates } : state.currentParcel,
      })),
      
      setRefCatastral: (refCatastral) => set({ refCatastral }),
      
      addToSearchHistory: (refCatastral) => set((state) => ({
        searchHistory: [
          refCatastral,
          ...state.searchHistory.filter(ref => ref !== refCatastral)
        ].slice(0, 10), // Mantener solo los últimos 10
      })),
      
      clearSearchHistory: () => set({ searchHistory: [] }),
      
      addFinca: (finca) => set((state) => ({
        fincas: [...state.fincas, finca],
        currentFinca: finca,
      })),
      
      removeFinca: (fincaId) => set((state) => ({
        fincas: state.fincas.filter(f => f.id !== fincaId),
        currentFinca: state.currentFinca?.id === fincaId ? null : state.currentFinca,
      })),
      
      setCurrentFinca: (finca) => set({ currentFinca: finca }),
      
      updateFinca: (fincaId, updates) => set((state) => ({
        fincas: state.fincas.map(f => f.id === fincaId ? { ...f, ...updates } : f),
        currentFinca: state.currentFinca?.id === fincaId ? { ...state.currentFinca, ...updates } : state.currentFinca,
      })),
      
      toggleShowParcels: () => set((state) => ({ showParcels: !state.showParcels })),
      toggleShowFincas: () => set((state) => ({ showFincas: !state.showFincas })),
      
      setLoading: (loading) => set({ isLoading: loading }),
      setSearching: (searching) => set({ isSearching: searching }),
      setError: (error) => set({ error }),
      
      reset: () => set(initialState),
    }),
    {
      name: 'cadastral-store',
    }
  )
);
